import type {
  ContractVersionView,
  ContractWorkflowResolution,
  WorkflowPurpose,
} from "@closer/shared";
import { WORKFLOW_PURPOSE_NAMES, WORKFLOW_PURPOSES } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeSurumSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * TANIMLI İŞ AKIŞINI BAŞLATMA (CLOS-1202).
 *
 * ### ⚠ Bu uç BUGÜNE KADAR EKRANSIZDI
 *
 * `POST /api/contracts/:id/workflow` canlıda çalışıyordu ve kullanıcının onu başlatacak
 * hiçbir yolu yoktu — canlı doğrulamada tarayıcı konsolundan çağrılmıştı. Ürün
 * "sözleşmen iç akıştan geçmeden karşı tarafa gidemez" diyor ve o akışı başlatacak
 * düğmeyi vermiyordu.
 *
 * ### ⚠ AKIŞ SEÇİLMİYOR, ÇÖZÜLÜYOR
 *
 * Hangi tanımın kullanılacağı çözümleme hiyerarşisinden geliyor (CLOS-1201: sözleşmeye özel
 * → tür varsayılanı → kiracı varsayılanı). Kullanıcıya tanım listesi açsaydık varsayılan
 * anlamını yitirir ve aynı soru iki yerden cevaplanırdı. Ekran yalnız ÇÖZÜLENİ gösteriyor.
 *
 * ### ⚠ AMAÇ SEÇİLİYOR, VARSAYILANI YOK
 *
 * Gönderim Onayı ile Nihai İç Onay iki ayrı karar (Karar Eki K-13/14/15) ve şema da
 * varsayılan tanımıyor. Birini önceden işaretleseydik kullanıcı hangi kararı verdiğini
 * okumadan başlatabilirdi.
 *
 * ### ⚠ ENGEL ÖNCEDEN SÖYLENİYOR
 *
 * Nihai iç onay yalnız mutabık versiyonda başlatılabilir (K-17). Sunucu bunu zaten
 * reddediyor; ekran ÖNCEDEN söylemezse kullanıcı formu doldurup 409 yer. Sebep metni
 * sunucunun kendi cümlesiyle aynı — iki farklı ifade, kullanıcıya iki farklı gerçek
 * gösterirdi.
 *
 * ### ⚠ Bu bileşen ELLE KURULAN AKIŞIN YERİNE GEÇMİYOR
 *
 * İç Onay bölümü iki yolu birden sunuyor: tanımlı akışı başlatmak (normal yol) ve
 * onaylayıcıları elle seçmek (mevcut yol). İkincisini kaldırmak, tanımlı akışı olmayan
 * kiracıların iç onayını tamamen kapatırdı.
 */
/**
 * ⚠ DIŞARIDAN ENGEL ALMIYOR ve bu CANLI DOĞRULAMADA öğrenildi.
 *
 * İlk yazımda bölümün `onKosul`'unu `disabled` olarak alıyordu. Ölçüldü: "kendi
 * başlattığın akışın tek onaylayıcısı olamazsın" kuralı `approvals.ts`'te — yani ELLE
 * kurulan yolda. `workflowInstance.ts` onu uygulamıyor, çünkü tanımlı akışın katılımcıları
 * tanımdan çözülüyor, kullanıcı seçmiyor.
 *
 * Yani o engeli buraya taşımak, sunucunun izin verdiği bir işi kullanıcıya yasaklamaktı —
 * bu dosyanın kendi yorumunun uyardığı hatanın ta kendisi. Tanımlı yolun engelleri modalın
 * içinde, çözümleme ucundan geliyor.
 *
 * ⚠ Arşiv durumu da burada değil: bölüm arşivde bu düğmeleri HİÇ çizmiyor.
 */
export function ContractWorkflowStart({ contractId }: { contractId: string }) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [amac, setAmac] = useState<WorkflowPurpose | "">("");
  const [sonTarih, setSonTarih] = useState("");
  const idempotency = useIdempotencyKey();

  const surumler = useQuery({
    enabled: acik,
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });
  /** ⚠ En son sürüm: akış GÜNCEL metin üzerinde başlar; eskisini seçtirmek karar değil kaza olurdu. */
  const guncelSurum: ContractVersionView | undefined = surumler.data?.[0];

  const cozumleme = useQuery({
    enabled: acik && Boolean(guncelSurum?.id),
    queryKey: tenantKey(tenantId, "contract", contractId, "akis-cozumleme", guncelSurum?.id ?? ""),
    queryFn: async () =>
      (
        await api<ContractWorkflowResolution>(
          `/api/contracts/${contractId}/workflow-resolution?versionId=${guncelSurum!.id}`,
          { tenantId },
        )
      ).data,
  });

  const secilen = amac === "" ? null : (cozumleme.data?.[amac] ?? null);

  const baslat = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/workflow`, {
        method: "POST",
        tenantId,
        /** ⚠ Uç `idempotency()` zinciriyle korunuyor: aynı başlatma iki kez yazılmasın. */
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify({
          versionId: guncelSurum!.id,
          workflowDefinitionId: secilen!.definitionId,
          dueDate: new Date(sonTarih).toISOString(),
          purpose: amac,
        }),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra yeni niyet yeni anahtar alıyor (BUG-004 dersi). */
      idempotency.yenile();
      toast.success("İş akışı başlatıldı");
      setAcik(false);
      setAmac("");
      setSonTarih("");
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", contractId),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İş akışı başlatılamadı"),
  });

  /**
   * ⚠ Gönderilebilirlik ÜÇ koşulun hepsini istiyor ve üçü de AYRI sebeplerle:
   * amaç seçilmiş olmalı (varsayılanı yok), çözümleme o amacı başlatılabilir bulmalı
   * (mutabakat/tanım engeli), son tarih girilmiş olmalı (ADR-0065 zorunlu).
   */
  const gonderilebilir =
    amac !== "" && secilen?.canStart === true && sonTarih !== "" && !baslat.isPending;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => setAcik(true)}
        data-testid="tanimli-akis-baslat"
      >
        Tanımlı akışı başlat
      </Button>

      <Modal open={acik} onClose={() => setAcik(false)} title="Tanımlı iş akışını başlat">
        <div className="flex flex-col gap-3">
          {/*
            ⚠ AMAÇ İLK SORU: hangi akışın çözüleceği amaca bağlı. Önce akışı gösterip sonra
            amacı sorsaydık, gösterilen akış seçimden sonra değişebilirdi.
          */}
          <FormField label="Akışın amacı">
            <Select
              value={amac}
              onChange={(e) => setAmac(e.target.value as WorkflowPurpose | "")}
              data-testid="akis-amaci"
            >
              <option value="">Seçin</option>
              {WORKFLOW_PURPOSES.map((p) => (
                <option key={p} value={p}>
                  {WORKFLOW_PURPOSE_NAMES[p]}
                </option>
              ))}
            </Select>
          </FormField>

          {cozumleme.isPending && acik && amac !== "" ? (
            <p className="text-sm text-fg-muted">İş akışı çözümleniyor…</p>
          ) : null}

          {secilen ? (
            <div className="flex flex-col gap-1 rounded-lg border border-border-default p-3">
              {/*
                ⚠ ÇÖZÜLEN AKIŞ ADIYLA gösteriliyor: kullanıcı neyi başlattığını bilmeli.
                Yalnız "başlat" deseydik, hangi kuralın işleyeceği görünmezdi.
              */}
              <p className="text-sm text-fg">
                {secilen.definitionName ?? "Tanımlı akış bulunamadı"}
              </p>
              {secilen.blockedReason ? (
                <p className="text-xs text-warning" data-testid="akis-cozumleme-engeli">
                  {secilen.blockedReason}
                </p>
              ) : null}
              {/*
                ⚠ BAŞLATMA ENGELİ AYRI BİR SATIR: "akış çözülemedi" ile "akış var ama bu
                versiyonda başlatılamaz" farklı sorunlar ve farklı çözümleri var.
              */}
              {secilen.blockedStartReason ? (
                <p className="text-xs text-warning" data-testid="akis-baslatma-engeli">
                  {secilen.blockedStartReason}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* ⚠ Son tarih ZORUNLU (ADR-0065): süresiz bir onay akışı hiç kapanmayabilir. */}
          <FormField label="Son tarih">
            <Input
              type="date"
              value={sonTarih}
              onChange={(e) => setSonTarih(e.target.value)}
              data-testid="akis-son-tarih"
            />
          </FormField>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setAcik(false)}>
              Vazgeç
            </Button>
            <Button
              type="button"
              onClick={() => baslat.mutate()}
              disabled={!gonderilebilir}
              data-testid="akis-baslat-onayla"
            >
              Başlat
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
